import { Injectable } from '@angular/core';
import { AbstractGatewayService, emailRegexp, CustomerStatus } from 'osn-shared';
import { AbstractEmailCollectorService } from 'projects/osn-shared/src/lib/components/email-collector/abstract-email-collector.service';
import { EmailCollectorViewModel } from 'projects/osn-shared/src/lib/components/email-collector/email-collector.view-model';
import { BehaviorSubject, catchError, combineLatest, forkJoin, map, Observable, of, tap } from 'rxjs';

@Injectable()
export class RedeemEmailCollectorService extends AbstractEmailCollectorService {
  private readonly _showTickMark$ = new BehaviorSubject<boolean>(false);
  public readonly showTickMark$ = this._showTickMark$.asObservable();
  public isLoading$ = new BehaviorSubject<boolean>(false);

  public showMobileProviderButton$ = of(false);

  constructor(private readonly gatewayService: AbstractGatewayService) {
    super();
  }

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  public startFreeTrial$(
    vm: EmailCollectorViewModel,
    email: string,
    password: string,
    voucher?: string,
    bin?: string
  ): Observable<EmailCollectorViewModel> {
    return this.getStartFreeTrialObservable$(vm, email, password, voucher, bin).pipe(
      tap(() => this.isLoading$.next(false)),
      catchError(() => {
        this.isLoading$.next(false);
        this.isRegisteredCredential = false;
        return of({ ...vm });
      })
    );
  }

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  private getStartFreeTrialObservable$(
    vm: EmailCollectorViewModel,
    email: string,
    password: string,
    voucher?: string,
    bin?: string
  ): Observable<EmailCollectorViewModel> {
    if (!vm.showPasswordInput) {
      if (!email.length) {
        return of({ ...vm, emailErrorMessage: 'Email_Collector_empty_email_error_message' });
      }
      // validate email format
      if (!emailRegexp.test(email)) {
        return of({ ...vm, emailErrorMessage: 'MLP_InvalidEmail_Label' });
      }

      this.isLoading$.next(true);
      if (!!voucher) {
        return forkJoin([
          this.gatewayService.validateVoucherCode$(voucher, email),
          this.gatewayService.createLeadCustomer$(email),
          this.gatewayService.getCustomerStatus$(),
        ]).pipe(
          map(([voucherResponse, emailResponse, customerStatus]) => {
            if (emailResponse.isUserStatusActive) {
              return { ...vm, emailErrorMessage: 'Redeem_Email_Collector_Email_Has_Subscription' };
            }

            if (emailResponse.valid) {
              this._showTickMark$.next(true);
            } else {
              this._showTickMark$.next(false);
            }

            if (voucherResponse.valid) {
              this.isRegisteredCredential = emailResponse.isRegisteredCredential;
              if (
                emailResponse.isRegisteredCredential &&
                !emailResponse.isUserStatusActive &&
                emailResponse.customerStatus === CustomerStatus.CHURNED
              ) {
                if (customerStatus === CustomerStatus.ANONYMOUS) {
                  this.gatewayService.login({ primaryCredential: email, voucherCode: voucher });
                } else {
                  this.gatewayService.signup(email, { voucher });
                }
                return {
                  ...vm,
                  emailErrorMessage: undefined,
                  additionalInputError: undefined,
                };
              }
              return {
                ...vm,
                showPasswordInput: !emailResponse.error,
                emailErrorMessage: emailResponse.error,
                additionalInputError: undefined,
              };
            } else {
              return { ...vm, emailErrorMessage: emailResponse.error, additionalInputError: voucherResponse.error };
            }
          })
        );
      }
      if (!!bin) {
        return combineLatest([this.gatewayService.validateBIN$(bin, email), this.gatewayService.createLeadCustomer$(email)]).pipe(
          map(([binResponse, emailResponse]) => {
            if (emailResponse.isUserStatusActive) {
              return { ...vm, emailErrorMessage: 'Redeem_Email_Collector_Email_Has_Subscription' };
            }
            if (binResponse.valid) {
              this.isRegisteredCredential = emailResponse.isRegisteredCredential;
              return {
                ...vm,
                showPasswordInput: !emailResponse.error,
                emailErrorMessage: emailResponse.error,
                additionalInputError: undefined,
              };
            } else {
              return { ...vm, emailErrorMessage: emailResponse.error, additionalInputError: binResponse.error };
            }
          })
        );
      }
      return of({ ...vm });
    } else {
      if (this.noWhiteSpaceRegex.test(password)) {
        return of({ ...vm, gatewayErrorMessage: 'Subscription_password_no_whitespace_error' });
      }
      this.isLoading$.next(true);
      const baseActionStream$ = this.isRegisteredCredential
        ? this.gatewayService.loginForOneTimeCode$(email, password)
        : this.gatewayService.createProspectCustomer$(email, password);
      return baseActionStream$.pipe(
        map((response) => {
          if (response.code) {
            this.gatewayService.signup(email, { oneTimeCode: response.code, voucher, bin });
            return { ...vm };
          } else if (response.error) {
            return { ...vm, gatewayErrorMessage: response.error };
          } else {
            return { ...vm };
          }
        })
      );
    }
  }

  public measureStartFreeTrialButtonClick(stepName: string, buttonText: string, displayOnDialog: boolean): void {}
  public measureSignUpWithMobileProviderButtonClick(buttonText: string, displayOnDialog: boolean): void {}
}
